import type { RouteObject } from "react-router";

import { ErrorBoundary } from "@/error-boundary";
import { AdminRoot } from "@/admin-root";
import { Root, rootLoader } from "@/root";

import { AboutPage, loader as aboutLoader, meta as aboutMeta } from "@/routes/about";
import { AdminPage, meta as adminMeta } from "@/routes/admin";
import { ContactPage, loader as contactLoader, meta as contactMeta } from "@/routes/contact";
import { GalleryPage, loader as galleryLoader, meta as galleryMeta } from "@/routes/gallery";
import { CharityTrekPage, loader as charityTrekLoader, meta as charityTrekMeta } from "@/routes/get-involved.charity-trek";
import { SupportPage, loader as supportLoader, meta as supportMeta } from "@/routes/get-involved.support";
import { GetInvolvedPage, loader as getInvolvedLoader, meta as getInvolvedMeta } from "@/routes/get-involved";
import { VolunteerPage, loader as volunteerLoader, meta as volunteerMeta } from "@/routes/get-involved.volunteer";
import { HomePage, loader as homeLoader, meta as homeMeta } from "@/routes/home";
import { ImpactPage, loader as impactLoader, meta as impactMeta } from "@/routes/impact";
import { MediaPage, loader as mediaLoader, meta as mediaMeta } from "@/routes/media";
import { NewsPage, loader as newsLoader, meta as newsMeta } from "@/routes/news";
import { ArticlePage, loader as articleLoader, meta as articleMeta } from "@/routes/news.$slug";
import { NotFoundPage, loader as notFoundLoader, meta as notFoundMeta } from "@/routes/not-found";
import { OurWorkPage, loader as ourWorkLoader, meta as ourWorkMeta } from "@/routes/our-work";
import { CmsPage, loader as cmsPageLoader, meta as cmsPageMeta } from "@/routes/page.$slug";
import { PillarPage, loader as pillarLoader, meta as pillarMeta } from "@/routes/our-work.$pillar";
import { PolicyPage, makePolicyRoute } from "@/routes/policy";
import { ProjectPage, loader as projectLoader, meta as projectMeta } from "@/routes/projects.$slug";
import { StoriesPage, loader as storiesLoader, meta as storiesMeta } from "@/routes/stories";
import { StoryPage, loader as storyLoader, meta as storyMeta } from "@/routes/stories.$slug";
import { TransparencyPage, loader as transparencyLoader, meta as transparencyMeta } from "@/routes/transparency";
import { WhereWeWorkPage, loader as whereWeWorkLoader, meta as whereWeWorkMeta } from "@/routes/where-we-work";

/**
 * The route table.
 *
 * Every path the previous build served still resolves. "Partner with us" became
 * "Support our work", and /get-involved/partner redirects rather than 404ing,
 * because that address is in search results and in documents HISCUS has already
 * sent out.
 *
 * Each page the app renders itself loads its own sections alongside the shared
 * bootstrap. The route decides the address; the page editor decides what is on
 * it and in what order.
 *
 * The four legal pages share one component. They are the same document type
 * with different words in them, and four hand-written pages would be four
 * places for the approval notice to be forgotten.
 *
 * The `meta` function on each route is what the server calls before rendering
 * to build the head, which is how a shared link still carries a title and a
 * preview image without JavaScript.
 */

const privacy = makePolicyRoute(
  "privacy",
  "Privacy",
  "What this website collects, what it does not collect, and the status of the formal HISCUS privacy policy.",
);
const terms = makePolicyRoute(
  "terms",
  "Terms and conditions",
  "The terms on which this website is provided, and the limits of what it offers.",
);
const safeguarding = makePolicyRoute(
  "safeguarding",
  "Safeguarding",
  "How HISCUS handles images, consent and concerns, and the status of the formal safeguarding policy.",
);
const cookies = makePolicyRoute("cookies", "Cookies and analytics", "Every cookie this website sets, and why.");

export const routes: RouteObject[] = [
  {
    /*
     * Above the public layout, not inside it. The admin panel gets none of the
     * site chrome and does not run the content bootstrap, because it needs
     * neither and the header's dropdowns would cover the editing forms.
     *
     * Every address under /admin renders the same shell. The panel reads the
     * rest of the address itself (/admin/pages/home, /admin/media), so a link
     * to one screen can be bookmarked or shared, and a reload lands where it was.
     */
    path: "/admin",
    Component: AdminRoot,
    ErrorBoundary,
    children: [
      { index: true, Component: AdminPage, handle: { meta: adminMeta } },
      { path: "*", Component: AdminPage, handle: { meta: adminMeta } },
    ],
  },
  {
    path: "/",
    Component: Root,
    loader: rootLoader,
    ErrorBoundary,
    children: [
      { index: true, Component: HomePage, loader: homeLoader, handle: { meta: homeMeta } },
      { path: "about", Component: AboutPage, loader: aboutLoader, handle: { meta: aboutMeta } },
      { path: "contact", Component: ContactPage, loader: contactLoader, handle: { meta: contactMeta } },
      { path: "gallery", Component: GalleryPage, loader: galleryLoader, handle: { meta: galleryMeta } },
      { path: "get-involved", Component: GetInvolvedPage, loader: getInvolvedLoader, handle: { meta: getInvolvedMeta } },
      {
        path: "get-involved/charity-trek",
        Component: CharityTrekPage,
        loader: charityTrekLoader,
        handle: { meta: charityTrekMeta },
      },
      { path: "get-involved/support", Component: SupportPage, loader: supportLoader, handle: { meta: supportMeta } },
      {
        path: "get-involved/volunteer",
        Component: VolunteerPage,
        loader: volunteerLoader,
        handle: { meta: volunteerMeta },
      },
      { path: "impact", Component: ImpactPage, loader: impactLoader, handle: { meta: impactMeta } },
      { path: "media", Component: MediaPage, loader: mediaLoader, handle: { meta: mediaMeta } },
      { path: "news", Component: NewsPage, loader: newsLoader, handle: { meta: newsMeta } },
      { path: "news/:slug", Component: ArticlePage, loader: articleLoader, handle: { meta: articleMeta } },
      { path: "our-work", Component: OurWorkPage, loader: ourWorkLoader, handle: { meta: ourWorkMeta } },
      { path: "our-work/:pillar", Component: PillarPage, loader: pillarLoader, handle: { meta: pillarMeta } },
      { path: "p/:slug", Component: CmsPage, loader: cmsPageLoader, handle: { meta: cmsPageMeta } },
      { path: "privacy", Component: PolicyPage, loader: privacy.loader, handle: { meta: privacy.meta } },
      { path: "terms", Component: PolicyPage, loader: terms.loader, handle: { meta: terms.meta } },
      { path: "cookies", Component: PolicyPage, loader: cookies.loader, handle: { meta: cookies.meta } },
      {
        path: "safeguarding",
        Component: PolicyPage,
        loader: safeguarding.loader,
        handle: { meta: safeguarding.meta },
      },
      { path: "projects/:slug", Component: ProjectPage, loader: projectLoader, handle: { meta: projectMeta } },
      { path: "stories", Component: StoriesPage, loader: storiesLoader, handle: { meta: storiesMeta } },
      { path: "stories/:slug", Component: StoryPage, loader: storyLoader, handle: { meta: storyMeta } },
      {
        path: "transparency",
        Component: TransparencyPage,
        loader: transparencyLoader,
        handle: { meta: transparencyMeta },
      },
      {
        path: "where-we-work",
        Component: WhereWeWorkPage,
        loader: whereWeWorkLoader,
        handle: { meta: whereWeWorkMeta },
      },
      { path: "*", Component: NotFoundPage, loader: notFoundLoader, handle: { meta: notFoundMeta } },
    ],
  },
];
